
import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface StatusDropdownProps {
  submissionId: string;
  currentStatus: string;
  onStatusChange?: (newStatus: string) => void;
  isPartial?: boolean;
}

const STATUS_OPTIONS = [
  'Screening',
  'Interviewing', 
  'Offer',
  'Hired',
  'Talent Pool',
  'Not selected'
];

const PARTIAL_STATUS_OPTIONS = [
  'partial',
  'Screening',
  'Interviewing', 
  'Offer',
  'Hired',
  'Talent Pool',
  'Not selected'
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'partial':
      return 'bg-orange-100 text-orange-800';
    case 'Screening':
      return 'bg-blue-100 text-blue-800';
    case 'Interviewing':
      return 'bg-yellow-100 text-yellow-800';
    case 'Offer':
      return 'bg-purple-100 text-purple-800';
    case 'Hired':
      return 'bg-green-100 text-green-800';
    case 'Talent Pool':
      return 'bg-gray-100 text-gray-800';
    case 'Not selected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status: string) => {
  return status === 'partial' ? 'Partial Assessment' : status;
};

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  submissionId,
  currentStatus,
  onStatusChange,
  isPartial = false
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === currentStatus) return;

    // Check authentication first
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to update candidate status.",
        variant: "destructive"
      });
      return;
    }

    // Check admin privileges
    if (!isAdmin()) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to update candidate status.",
        variant: "destructive"
      });
      return;
    }

    // Store previous status for potential rollback
    const previousStatus = currentStatus;
    
    // Optimistic update: Update UI immediately
    onStatusChange?.(newStatus);
    setIsUpdating(true);
    
    try {
      console.log('Current user:', user.id);
      console.log('Updating status for submission:', submissionId, 'to:', newStatus);
      
      const { error } = await supabase
        .from('survey_submissions')
        .update({ status: newStatus })
        .eq('id', submissionId);

      if (error) {
        console.error('Database error:', error);
        
        // Rollback the optimistic update
        onStatusChange?.(previousStatus);
        
        // Handle specific error types with better user messaging
        if (error.code === 'PGRST301' || error.message?.includes('row-level security')) {
          toast({
            title: "Permission Denied",
            description: "You don't have permission to update candidate status. Please check with your administrator.",
            variant: "destructive"
          });
        } else if (error.code === '23503') {
          toast({
            title: "Invalid Status",
            description: "The selected status is not valid for this candidate.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Update Failed",
            description: `Failed to update status: ${error.message}`,
            variant: "destructive"
          });
        }
        return;
      }

      console.log('Status updated successfully');
      
      toast({
        title: "Status Updated",
        description: `Status changed to ${newStatus}`,
      });
    } catch (error: any) {
      console.error('Error updating status:', error);
      
      // Rollback the optimistic update
      onStatusChange?.(previousStatus);
      
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Select
      value={currentStatus}
      onValueChange={handleStatusChange}
      disabled={isUpdating || !isAdmin()}
    >
      <SelectTrigger className="w-32 h-auto p-0 border-none bg-transparent hover:bg-gray-50 [&>svg]:hidden">
        <SelectValue asChild>
          <Badge className={`${getStatusColor(currentStatus)} text-xs font-medium cursor-pointer`}>
            {isUpdating ? 'Updating...' : getStatusLabel(currentStatus)}
          </Badge>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {(isPartial ? PARTIAL_STATUS_OPTIONS : STATUS_OPTIONS).map((status) => (
          <SelectItem key={status} value={status}>
            <Badge className={`${getStatusColor(status)} text-xs font-medium`}>
              {getStatusLabel(status)}
            </Badge>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default StatusDropdown;
